import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    Search,
    Plus,
    ChevronLeft,
    ChevronRight,
    Check,
    X,
    Pencil
} from "lucide-react";

import api from "../services/api";


function Products() {

    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [permissions, setPermissions] = useState([]);
    const [userRole, setUserRole] = useState("");

    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [savingCell, setSavingCell] = useState(false);

    const [showCreateProduct, setShowCreateProduct] =
        useState(false);

    const [newProductName, setNewProductName] =
        useState("");

    const [newProductPrice, setNewProductPrice] =
        useState("");

    const [creatingProduct, setCreatingProduct] =
        useState(false);


    // ========================================
    // ACCESS
    // ========================================

    const loadAccess = async () => {

        try {

            const response =
                await api.get("/auth/me");

            setUserRole(
                response.data.user?.role || ""
            );

            setPermissions(
                response.data.permissions || []
            );

        } catch (error) {

            console.error(
                "Unable to load permissions",
                error
            );
        }
    };


    const hasPermission = (permissionKey) => {

        if (userRole === "OWNER") {
            return true;
        }

        return permissions.some(
            permission =>
                permission.permission_key ===
                permissionKey
        );
    };


    // ========================================
    // COLUMN VISIBILITY
    // ========================================

    const canViewCTN =
        hasPermission("VIEW_CTN");

    const canViewQuantity =
        hasPermission("VIEW_QUANTITY");

    const canViewUnit =
        hasPermission("VIEW_UNIT");

    const canViewCostPrice =
        hasPermission("VIEW_COST_PRICE");

    const canViewSellingPrice =
        hasPermission("VIEW_SELLING_PRICE");

    const canViewLocation =
        hasPermission("VIEW_LOCATION");


    // ========================================
    // LOAD PRODUCTS
    // ========================================

    const loadProducts = async (
        selectedPage = 1,
        searchText = ""
    ) => {

        try {

            setLoading(true);
            setError("");

            const params = {
                page: selectedPage,
                limit: 20
            };

            if (searchText.trim()) {
                params.name =
                    searchText.trim();
            }

            const response =
                await api.get(
                    "/products",
                    { params }
                );

            setProducts(
                response.data.products || []
            );

            setPage(
                response.data.page || 1
            );

            setTotalPages(
                response.data.total_pages || 1
            );

            setTotalProducts(
                response.data.total_products || 0
            );

        } catch (error) {

            setError(
                error.response?.data?.message ||
                "Unable to load products"
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadAccess();

    }, []);


    useEffect(() => {

        const timer =
            setTimeout(() => {

                loadProducts(
                    1,
                    search
                );

            }, 300);

        return () =>
            clearTimeout(timer);

    }, [search]);


    // ========================================
    // FORMATTERS
    // ========================================

    const formatNumber = (
        value,
        maximumFractionDigits = 2
    ) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "—";
        }

        const numericValue =
            Number(value);

        if (!Number.isFinite(numericValue)) {
            return String(value);
        }

        return numericValue.toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 0,
                maximumFractionDigits
            }
        );
    };


    const formatPrice = (value) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "—";
        }

        return `TSh ${formatNumber(value, 2)}`;
    };


    const displayText = (value) => {

        if (
            value === null ||
            value === undefined ||
            String(value).trim() === ""
        ) {
            return "—";
        }

        return String(value);
    };


    // ========================================
    // EDIT CELL
    // ========================================

    const startEdit = (
        product,
        field
    ) => {

        // Product name + new detail fields
        // are owner-editable only.

        if (
            field !== "retail_price" &&
            userRole !== "OWNER"
        ) {
            return;
        }

        // SP can be edited by owner or staff
        // with EDIT_PRICES + visibility.

        if (
            field === "retail_price" &&
            !hasPermission("EDIT_PRICES")
        ) {
            return;
        }

        setEditingCell({
            productId: product.id,
            field
        });

        const currentValue =
            product[field];

        setEditValue(
            currentValue === null ||
            currentValue === undefined
                ? ""
                : String(currentValue)
        );
    };


    const cancelEdit = () => {

        if (savingCell) {
            return;
        }

        setEditingCell(null);
        setEditValue("");
    };


    // ========================================
    // SAVE CELL
    // ========================================

    const saveCell = async (product) => {

        if (
            !editingCell ||
            savingCell
        ) {
            return;
        }

        const field =
            editingCell.field;

        try {

            setSavingCell(true);


            // ========================================
            // PRODUCT NAME
            // ========================================

            if (field === "product_name") {

                const cleanName =
                    editValue
                        .trim()
                        .replace(/\s+/g, " ");

                if (!cleanName) {

                    alert(
                        "Product name cannot be empty."
                    );

                    return;
                }

                await api.patch(
                    `/products/${product.id}/name`,
                    {
                        product_name:
                            cleanName
                    }
                );
            }


            // ========================================
            // SELLING PRICE
            // ========================================

            else if (
                field === "retail_price"
            ) {

                if (
                    editValue === "" ||
                    !Number.isFinite(
                        Number(editValue)
                    ) ||
                    Number(editValue) < 0
                ) {

                    alert(
                        "Please enter a valid selling price."
                    );

                    return;
                }

                await api.patch(
                    `/products/${product.id}/price`,
                    {
                        retail_price:
                            Number(editValue)
                    }
                );
            }


            // ========================================
            // PRODUCT DETAILS
            // ========================================

            else {

                let value =
                    editValue;

                if (
                    field === "ctn" ||
                    field === "quantity" ||
                    field === "cost_price"
                ) {

                    if (editValue === "") {

                        value = null;

                    } else {

                        const numericValue =
                            Number(editValue);

                        if (
                            !Number.isFinite(
                                numericValue
                            ) ||
                            numericValue < 0
                        ) {

                            alert(
                                "Please enter a valid non-negative number."
                            );

                            return;
                        }

                        value =
                            numericValue;
                    }

                } else {

                    value =
                        editValue.trim() === ""
                            ? null
                            : editValue.trim();
                }


                await api.patch(
                    `/products/${product.id}/details`,
                    {
                        [field]:
                            value
                    }
                );
            }


            setEditingCell(null);
            setEditValue("");

            await loadProducts(
                page,
                search
            );


        } catch (error) {

            alert(
                error.response?.data?.message ||
                "Unable to save product"
            );

        } finally {

            setSavingCell(false);
        }
    };


    // ========================================
    // CREATE PRODUCT
    // ========================================

    const openCreateProduct = () => {

        setNewProductName("");
        setNewProductPrice("");
        setShowCreateProduct(true);
    };


    const closeCreateProduct = () => {

        if (creatingProduct) {
            return;
        }

        setShowCreateProduct(false);
        setNewProductName("");
        setNewProductPrice("");
    };


    const createProduct = async () => {

        const cleanName =
            newProductName
                .trim()
                .replace(/\s+/g, " ");

        const numericPrice =
            Number(newProductPrice);


        if (!cleanName) {

            alert(
                "Please enter a product name."
            );

            return;
        }


        if (
            newProductPrice === "" ||
            !Number.isFinite(
                numericPrice
            ) ||
            numericPrice < 0
        ) {

            alert(
                "Please enter a valid selling price."
            );

            return;
        }


        try {

            setCreatingProduct(true);


            await api.post(
                "/products",
                {
                    product_name:
                        cleanName,

                    retail_price:
                        numericPrice
                }
            );


            setShowCreateProduct(false);
            setNewProductName("");
            setNewProductPrice("");

            setSearch("");

            await loadProducts(
                1,
                ""
            );


        } catch (error) {

            alert(
                error.response?.data?.message ||
                "Unable to create product"
            );

        } finally {

            setCreatingProduct(false);
        }
    };


    // ========================================
    // EDITABLE CELL COMPONENT
    // ========================================

    const EditableCell = ({
        product,
        field,
        type = "text",
        children,
        canEdit = false
    }) => {

        const isEditing =
            editingCell?.productId ===
                product.id &&
            editingCell?.field ===
                field;


        if (isEditing) {

            return (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px"
                    }}
                >

                    <input
                        type={type}
                        min={
                            type === "number"
                                ? "0"
                                : undefined
                        }
                        value={editValue}
                        autoFocus
                        onChange={(e) =>
                            setEditValue(
                                e.target.value
                            )
                        }
                        onKeyDown={(e) => {

                            if (
                                e.key === "Enter"
                            ) {
                                saveCell(
                                    product
                                );
                            }

                            if (
                                e.key === "Escape"
                            ) {
                                cancelEdit();
                            }
                        }}
                        style={{
                            width:
                                field === "location"
                                    ? "130px"
                                    : field === "product_name"
                                    ? "180px"
                                    : "90px",
                            padding: "7px 8px",
                            border:
                                "1px solid #b9b9b9",
                            borderRadius: "7px",
                            fontSize: "13px"
                        }}
                    />


                    <button
                        type="button"
                        onClick={() =>
                            saveCell(
                                product
                            )
                        }
                        disabled={
                            savingCell
                        }
                        title="Save"
                        style={{
                            border: "none",
                            background:
                                "transparent",
                            cursor: "pointer",
                            padding: "3px"
                        }}
                    >
                        <Check size={17} />
                    </button>


                    <button
                        type="button"
                        onClick={
                            cancelEdit
                        }
                        disabled={
                            savingCell
                        }
                        title="Cancel"
                        style={{
                            border: "none",
                            background:
                                "transparent",
                            cursor: "pointer",
                            padding: "3px"
                        }}
                    >
                        <X size={17} />
                    </button>

                </div>
            );
        }


        return (
            <button
                type="button"
                onClick={() => {

                    if (canEdit) {
                        startEdit(
                            product,
                            field
                        );
                    }
                }}
                style={{
                    border: "none",
                    background:
                        "transparent",
                    padding: 0,
                    margin: 0,
                    font: "inherit",
                    color: "inherit",
                    cursor:
                        canEdit
                            ? "pointer"
                            : "default",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    textAlign: "left",
                    whiteSpace: "nowrap"
                }}
            >

                {children}

                {canEdit && (
                    <Pencil
                        size={12}
                        style={{
                            opacity: 0.45
                        }}
                    />
                )}

            </button>
        );
    };


    return (
        <div className="mobile-app">


            {/* HEADER */}

            <header className="page-header">

                <button
                    className="back-button"
                    onClick={() =>
                        navigate(
                            "/dashboard"
                        )
                    }
                >
                    <ArrowLeft
                        size={21}
                    />
                </button>


                <div>

                    <h1>
                        Products
                    </h1>

                    <p>
                        {totalProducts}{" "}
                        {totalProducts === 1
                            ? "product"
                            : "products"
                        }
                    </p>

                </div>

            </header>


            <main className="products-content">


                {/* SEARCH */}

                <div className="product-search">

                    <Search
                        size={20}
                        className="search-icon"
                    />

                    <input
                        type="text"
                        placeholder="Search products..."
                        value={search}
                        onChange={(e) =>
                            setSearch(
                                e.target.value
                            )
                        }
                        autoComplete="off"
                    />

                    {search && (

                        <button
                            type="button"
                            className="clear-search"
                            onClick={() =>
                                setSearch("")
                            }
                        >
                            ×
                        </button>

                    )}

                </div>


                {/* ADD PRODUCT */}

                {hasPermission(
                    "CREATE_PRODUCTS"
                ) && (

                    <button
                        type="button"
                        className="create-product-button"
                        onClick={
                            openCreateProduct
                        }
                    >
                        <Plus size={18} />
                        Add Product
                    </button>

                )}


                {error && (

                    <div className="error-message">
                        {error}
                    </div>

                )}


                {/* TABLE */}

                {loading ? (

                    <div className="loading-state">
                        Loading products...
                    </div>

                ) : products.length === 0 ? (

                    <div className="empty-state">
                        No products found.
                    </div>

                ) : (

                    <div
                        style={{
                            width: "100%",
                            overflowX: "auto",
                            WebkitOverflowScrolling:
                                "touch",
                            marginTop: "14px",
                            borderRadius: "12px",
                            border:
                                "1px solid #e7e7e7",
                            background: "#fff"
                        }}
                    >

                        <table
                            style={{
                                width: "100%",
                                minWidth: "800px",
                                borderCollapse:
                                    "collapse",
                                fontSize: "13px"
                            }}
                        >

                            <thead>

                                <tr
                                    style={{
                                        background:
                                            "#f7f7f7"
                                    }}
                                >

                                    <th
                                        style={
                                            headerStyle
                                        }
                                    >
                                        Product Name
                                    </th>


                                    {canViewCTN && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            CTN
                                        </th>
                                    )}


                                    {canViewQuantity && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            QNT
                                        </th>
                                    )}


                                    {canViewUnit && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Unit
                                        </th>
                                    )}


                                    {canViewCostPrice && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            CP
                                        </th>
                                    )}


                                    {canViewSellingPrice && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            SP
                                        </th>
                                    )}


                                    {canViewLocation && (
                                        <th
                                            style={
                                                headerStyle
                                            }
                                        >
                                            Location
                                        </th>
                                    )}

                                </tr>

                            </thead>


                            <tbody>

                                {products.map(
                                    product => {

                                        const productNameColor =
                                            product.stock_indicator === "IN_STOCK"
                                                ? "#15803d"
                                                : product.stock_indicator === "OUT_OF_STOCK"
                                                ? "#dc2626"
                                                : "inherit";

                                        return (

                                            <tr
                                                key={
                                                    product.id
                                                }
                                                style={{
                                                    borderTop:
                                                        "1px solid #eeeeee"
                                                }}
                                            >

                                                {/* PRODUCT NAME */}

                                                <td
                                                    style={
                                                        cellStyle
                                                    }
                                                >

                                                    <EditableCell
                                                        product={
                                                            product
                                                        }
                                                        field="product_name"
                                                        canEdit={
                                                            userRole ===
                                                            "OWNER"
                                                        }
                                                    >
                                                        <strong
                                                            style={{
                                                                color:
                                                                    productNameColor
                                                            }}
                                                        >
                                                            {
                                                                product.product_name
                                                            }
                                                        </strong>
                                                    </EditableCell>

                                                </td>


                                                {/* CTN */}

                                                {canViewCTN && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="ctn"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                formatNumber(
                                                                    product.ctn
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* QUANTITY */}

                                                {canViewQuantity && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="quantity"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                formatNumber(
                                                                    product.quantity
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* UNIT */}

                                                {canViewUnit && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="unit"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                displayText(
                                                                    product.unit
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* COST PRICE */}

                                                {canViewCostPrice && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="cost_price"
                                                            type="number"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                formatPrice(
                                                                    product.cost_price
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* SELLING PRICE */}

                                                {canViewSellingPrice && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="retail_price"
                                                            type="number"
                                                            canEdit={
                                                                hasPermission(
                                                                    "EDIT_PRICES"
                                                                )
                                                            }
                                                        >
                                                            <strong>
                                                                {
                                                                    formatPrice(
                                                                        product.retail_price
                                                                    )
                                                                }
                                                            </strong>
                                                        </EditableCell>

                                                    </td>

                                                )}


                                                {/* LOCATION */}

                                                {canViewLocation && (

                                                    <td
                                                        style={
                                                            cellStyle
                                                        }
                                                    >

                                                        <EditableCell
                                                            product={
                                                                product
                                                            }
                                                            field="location"
                                                            canEdit={
                                                                userRole ===
                                                                "OWNER"
                                                            }
                                                        >
                                                            {
                                                                displayText(
                                                                    product.location
                                                                )
                                                            }
                                                        </EditableCell>

                                                    </td>

                                                )}

                                            </tr>

                                        );
                                    }
                                )}

                            </tbody>

                        </table>

                    </div>

                )}


                {/* PAGINATION */}

                {!loading &&
                    products.length > 0 &&
                    totalPages > 1 && (

                    <div className="pagination">

                        <button
                            disabled={
                                page <= 1
                            }
                            onClick={() =>
                                loadProducts(
                                    page - 1,
                                    search
                                )
                            }
                        >
                            <ChevronLeft
                                size={19}
                            />
                        </button>


                        <span>
                            Page {page} of{" "}
                            {totalPages}
                        </span>


                        <button
                            disabled={
                                page >=
                                totalPages
                            }
                            onClick={() =>
                                loadProducts(
                                    page + 1,
                                    search
                                )
                            }
                        >
                            <ChevronRight
                                size={19}
                            />
                        </button>

                    </div>

                )}

            </main>


            {/* CREATE PRODUCT POPUP */}

            {showCreateProduct && (

                <div
                    className="product-modal-overlay"
                    onClick={
                        closeCreateProduct
                    }
                >

                    <div
                        className="product-modal create-product-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="modal-handle" />


                        <div className="product-modal-header">

                            <div>
                                <p>
                                    New Product
                                </p>

                                <h2>
                                    Add Product
                                </h2>
                            </div>


                            <button
                                type="button"
                                className="modal-close"
                                onClick={
                                    closeCreateProduct
                                }
                            >
                                <X size={20} />
                            </button>

                        </div>


                        <div className="form-group">

                            <label>
                                Product Name
                            </label>

                            <input
                                type="text"
                                placeholder="Enter product name"
                                value={
                                    newProductName
                                }
                                onChange={(e) =>
                                    setNewProductName(
                                        e.target.value
                                    )
                                }
                                autoComplete="off"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Selling Price (SP)
                            </label>

                            <div className="create-price-input">

                                <span>
                                    TSh
                                </span>

                                <input
                                    type="number"
                                    min="0"
                                    placeholder="15000"
                                    value={
                                        newProductPrice
                                    }
                                    onChange={(e) =>
                                        setNewProductPrice(
                                            e.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        <button
                            type="button"
                            className="primary-button"
                            onClick={
                                createProduct
                            }
                            disabled={
                                creatingProduct
                            }
                        >

                            {creatingProduct
                                ? "Creating..."
                                : "Create Product"
                            }

                        </button>

                    </div>

                </div>

            )}

        </div>
    );
}


// ========================================
// TABLE STYLES
// ========================================

const headerStyle = {
    padding: "12px 14px",
    textAlign: "left",
    fontWeight: 700,
    whiteSpace: "nowrap",
    color: "#444"
};


const cellStyle = {
    padding: "12px 14px",
    textAlign: "left",
    verticalAlign: "middle",
    whiteSpace: "nowrap"
};


export default Products;